import { getStoredHostSession } from "../webAuth";

// The host panel's conversation with the Q&A service.
//
// Separate from the audience client because the credentials differ and the
// consequences of getting them wrong differ: an audience member holds a room
// code, a host holds an account and/or a deck key, and BOTH are sent on every
// call. An account must never authorise less than the key alone — dropping the
// key once locked an anonymous host out of their own deck the moment they signed
// in.

// In PRODUCTION this IS set — .env.production points it at api2.copresent.app,
// the Node service's own hostname. The plan to serve it as api.copresent.app/qa/*
// so no client knew a second host never shipped (Enterprise-only Cloudflare
// override), so the clicker calls that second host directly.
//
// The fallback to REACT_APP_API_URL is the local story: point it at the Node
// service's own port, or leave it unset and reach Q&A through the C# backend's
// dev proxy on the one host. Fallback, never a default — a value baked in here
// would quietly become a second production origin.
const API_URL =
  process.env.REACT_APP_QA_API_URL ?? process.env.REACT_APP_API_URL ?? "";

export interface HostQuestion {
  readonly id: string;
  readonly text: string;
  readonly authorName: string | null;
  readonly slideIndex: number | null;
  readonly votes: number;
  readonly answered: boolean;
  readonly pinned: boolean;
  readonly hidden: boolean;
  readonly askedAt: string;
}

export type HostQaError =
  /** Signing in is all they need — Q&A is on every plan. */
  | "sign_in_required"
  | "upgrade_required"
  | "needs_host_link"
  | "no_session"
  | "offline";

export class HostQaRequestError extends Error {
  constructor(readonly reason: HostQaError) {
    super(reason);
  }
}

const REASONS: Record<number, HostQaError> = {
  401: "sign_in_required",
  402: "upgrade_required",
  403: "needs_host_link",
  404: "no_session",
};

/**
 * The credential, in one place.
 *
 * The session token is the only one now. The extension-minted host key
 * that used to ride alongside it in X-Copresent-Host-Key is retired with the
 * anonymous host controller — Q&A always required an account anyway, since a
 * plan cannot hang on a host who does not have one.
 *
 * It still travels in a header rather than a query string, which is why the
 * stream below is read with fetch() rather than EventSource — EventSource
 * cannot set headers at all.
 */
function headers(hasBody = false): Record<string, string> {
  const session = getStoredHostSession();
  return {
    // Only when there IS one. Declaring a JSON body and then sending none is
    // what a strict server is entitled to reject, and Fastify does — POST /open
    // takes no arguments, so announcing a body it never sends made every "Turn
    // on Q&A" fail with a 400 the panel reported as "couldn't reach the server".
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(session ? { Authorization: `Bearer ${session.sessionToken}` } : {}),
  };
}

async function send(path: string, init: RequestInit): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}/qa${path}`, {
      ...init,
      headers: headers(init.body != null),
    });
  } catch {
    throw new HostQaRequestError("offline");
  }

  if (!response.ok) {
    throw new HostQaRequestError(REASONS[response.status] ?? "offline");
  }
  return response;
}

export interface RoomState {
  readonly code: string;
  readonly open: boolean;
}

export interface OverlayState {
  readonly visible: boolean;
  /** Absolute ISO instant, or null for "until I turn it off". */
  readonly hideAt: string | null;
}

/**
 * Where the audience lands. The short host is the whole point of a
 * six-character code — copresent.app redirects /q/:code to the clicker — so
 * this must never be built from window.location.
 */
const AUDIENCE_URL = process.env.REACT_APP_AUDIENCE_URL ?? "https://copresent.app/q";

export const audienceUrlFor = (code: string): string => `${AUDIENCE_URL}/${code}`;

/**
 * The same base, without the scheme — "copresent.app/q".
 *
 * What a QR is shown WITH, everywhere one appears: someone at the back cannot
 * scan, someone on a laptop has no camera to hand, and a QR with no address
 * beside it leaves them nothing to type. Kept next to audienceUrlFor so the two
 * can never name different hosts.
 */
export const audienceDisplayBase = AUDIENCE_URL.replace(/^https?:\/\//, "");

/**
 * Raise or drop the code on the presented screen.
 *
 * `hideAfterSeconds` null means "stay until turned off". Sending it again while
 * visible restarts the timer, which is what a host means when they flash the
 * code a second time for latecomers.
 */
export async function setOverlay(
  magicId: string,
  visible: boolean,
  hideAfterSeconds: number | null,
): Promise<OverlayState> {
  const response = await send(`/sessions/${encodeURIComponent(magicId)}/overlay`, {
    method: "PATCH",
    body: JSON.stringify({
      visible,
      ...(hideAfterSeconds != null ? { hideAfterSeconds } : {}),
    }),
  });
  return (await response.json()) as OverlayState;
}

/**
 * The room this session already has, if any.
 *
 * Called on mount so a refreshed controller comes back to the room it left
 * rather than to the "Start Q&A" button — the panel's state lives in the
 * component, and a reload throws all of it away while the code stays on the
 * projector and the audience keeps typing.
 *
 * Null, not a throw, when there is no room: "this host has never opened Q&A"
 * and "this session is gone" are the same answer to the only question the panel
 * is asking, and both mean it should render its ordinary starting state.
 */
export async function getQaRoom(
  magicId: string,
): Promise<(RoomState & { readonly overlay: OverlayState }) | null> {
  try {
    const response = await send(`/sessions/${encodeURIComponent(magicId)}`, {
      method: "GET",
    });
    return (await response.json()) as RoomState & { overlay: OverlayState };
  } catch (error) {
    if (error instanceof HostQaRequestError && error.reason === "no_session") return null;
    throw error;
  }
}

export async function openQa(magicId: string): Promise<RoomState> {
  const response = await send(`/sessions/${encodeURIComponent(magicId)}/open`, {
    method: "POST",
  });
  return (await response.json()) as RoomState;
}

/**
 * End it: the code is retired and the questions are deleted.
 *
 * Distinct from setQaOpen(false), which only pauses — that keeps the code so the
 * room can be resumed onto the same printed QR. This returns the session to
 * having no room at all, so the panel goes back to its empty state and the next
 * "Start Q&A" mints a new code for a new audience.
 */
export async function closeQa(magicId: string): Promise<void> {
  await send(`/sessions/${encodeURIComponent(magicId)}/close`, {
    method: "POST",
  });
}

export async function setQaOpen(
  magicId: string,
  open: boolean,
): Promise<RoomState> {
  const response = await send(`/sessions/${encodeURIComponent(magicId)}`, {
    method: "PATCH",
    body: JSON.stringify({ open }),
  });
  return (await response.json()) as RoomState;
}

export async function moderateQuestion(
  magicId: string,
  questionId: string,
  patch: { hidden?: boolean; answered?: boolean; pinned?: boolean },
): Promise<void> {
  await send(
    `/sessions/${encodeURIComponent(magicId)}/questions/${encodeURIComponent(questionId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

/**
 * Watch the room's questions.
 *
 * Reads a Server-Sent Events stream with fetch() rather than EventSource,
 * because EventSource cannot send the host key header. The first frame is a full
 * snapshot, so there is no separate initial GET to race against.
 *
 * Every stream ends: Cloud Run terminates requests at its timeout, so being cut
 * mid-presentation is routine rather than exceptional. `onEnd` fires whenever
 * that happens and the caller reconnects — deliberately NOT on a timer here, so
 * the reconnect policy lives with the component that knows whether the panel is
 * still on screen.
 */
export function streamQuestions(
  magicId: string,
  handlers: {
    onQuestions: (questions: HostQuestion[]) => void;
    onError: (reason: HostQaError) => void;
    onEnd: () => void;
  },
): () => void {
  const controller = new AbortController();
  let stopped = false;

  const run = async () => {
    let response: Response;
    try {
      response = await fetch(
        `${API_URL}/qa/sessions/${encodeURIComponent(magicId)}/stream`,
        { headers: headers(), signal: controller.signal },
      );
    } catch {
      if (!stopped) handlers.onError("offline");
      if (!stopped) handlers.onEnd();
      return;
    }

    if (!response.ok || !response.body) {
      if (!stopped) handlers.onError(REASONS[response.status] ?? "offline");
      if (!stopped) handlers.onEnd();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done || stopped) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line. Anything after the last one
        // is a partial frame and stays in the buffer until the rest arrives —
        // parsing it early is how a stream reader drops every message that
        // happens to straddle a chunk boundary.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";

        for (const frame of frames) {
          // Comment frames (": keepalive") carry no data and are skipped.
          const dataLine = frame.split("\n").find((line) => line.startsWith("data: "));
          if (!dataLine) continue;

          try {
            const payload = JSON.parse(dataLine.slice("data: ".length));
            if (Array.isArray(payload?.questions)) handlers.onQuestions(payload.questions);
          } catch {
            // A malformed frame is not worth tearing the stream down for.
          }
        }
      }
    } catch {
      if (!stopped) handlers.onError("offline");
    }

    if (!stopped) handlers.onEnd();
  };

  void run();

  return () => {
    stopped = true;
    controller.abort();
  };
}
